import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * "Do what you love" reward boost.
 *
 * When a user has chosen a contribution path (their Calling), any action
 * tagged to that path earns +50% XP and +50% Kingdom Tokens.
 *
 * Action types are auto-classified by keyword so existing call sites
 * receive the bonus without any code changes.
 */
export const CALLING_BONUS_MULTIPLIER = 1.5;

/**
 * Anti-farming guardrails for the Calling Bonus.
 * - Daily diminishing returns per `action_type`:
 *     reps 1–3 → 1.5×, reps 4–5 → 1.2×, reps 6+ → 1.0× (no bonus)
 * - Cooldown between back-to-back bonuses for the same `action_type`.
 */
export const CALLING_BONUS_COOLDOWN_SECONDS = 60;
const DIMINISHING_TIERS: { maxRepsInTier: number; multiplier: number }[] = [
  { maxRepsInTier: 3, multiplier: 1.5 }, // first 3 boosted reps today
  { maxRepsInTier: 5, multiplier: 1.2 }, // reps 4–5
  { maxRepsInTier: Infinity, multiplier: 1.0 }, // 6+ → no bonus
];

/** Marker we write into descriptions so we can count prior boosted reps. */
export const CALLING_BONUS_MARKER = "Calling Bonus";

export type ContributionPath = "trade" | "real_estate" | "business";

/** Classify an arbitrary action / ledger type into a contribution path. */
export function classifyActionPath(actionType: string): ContributionPath | null {
  const a = (actionType || "").toLowerCase();
  if (/(trade|paper_trade|chart|wheel|option|market|swell|gex)/.test(a)) return "trade";
  if (/(property|real_estate|realestate|rent|rental|flip|estate|landlord|underwrite)/.test(a)) return "real_estate";
  if (/(business|thesis|pitch|venture|founder|sales|ops|sop|product_launch)/.test(a)) return "business";
  return null;
}

/**
 * Compute today's boosted-rep count for an action and return the effective
 * multiplier after diminishing returns + cooldown.
 *
 * `table`: which ledger to inspect — XP transactions vs Kingdom Tokens.
 *
 * Returns 1 (no bonus) when:
 *  - no calling set,
 *  - the action doesn't match the calling,
 *  - the per-action cooldown hasn't elapsed since the last bonus,
 *  - or the user has exhausted the daily diminishing tiers for this action.
 */
export async function resolveBoostedMultiplier(params: {
  userId: string;
  actionType: string;
  callingPath: ContributionPath | null;
  ledger: "xp" | "kt";
}): Promise<{ multiplier: number; reason: "no_calling" | "wrong_path" | "cooldown" | "exhausted" | "ok" }> {
  const { userId, actionType, callingPath, ledger } = params;
  if (!callingPath) return { multiplier: 1, reason: "no_calling" };
  const cls = classifyActionPath(actionType);
  if (!cls || cls !== callingPath) return { multiplier: 1, reason: "wrong_path" };

  const dayStart = new Date();
  dayStart.setHours(0, 0, 0, 0);

  if (ledger === "xp") {
    const { data } = await supabase
      .from("xp_transactions")
      .select("created_at, description")
      .eq("user_id", userId)
      .eq("action_type", actionType)
      .gte("created_at", dayStart.toISOString())
      .order("created_at", { ascending: false })
      .limit(50);

    const boosted = (data ?? []).filter((r: any) =>
      typeof r.description === "string" && r.description.includes(CALLING_BONUS_MARKER)
    );
    if (boosted.length > 0) {
      const last = new Date(boosted[0].created_at).getTime();
      if ((Date.now() - last) / 1000 < CALLING_BONUS_COOLDOWN_SECONDS) {
        return { multiplier: 1, reason: "cooldown" };
      }
    }
    return resolveTierMultiplier(boosted.length);
  }

  // KT ledger — same logic, different table/column conventions.
  const { data } = await (supabase as any)
    .from("kingdom_tokens_ledger")
    .select("created_at, description")
    .eq("user_id", userId)
    .eq("type", actionType)
    .gte("created_at", dayStart.toISOString())
    .order("created_at", { ascending: false })
    .limit(50);

  const boosted = (data ?? []).filter((r: any) =>
    typeof r.description === "string" && r.description.includes(CALLING_BONUS_MARKER)
  );
  if (boosted.length > 0) {
    const last = new Date(boosted[0].created_at).getTime();
    if ((Date.now() - last) / 1000 < CALLING_BONUS_COOLDOWN_SECONDS) {
      return { multiplier: 1, reason: "cooldown" };
    }
  }
  return resolveTierMultiplier(boosted.length);
}

function resolveTierMultiplier(priorBoostedCount: number):
  { multiplier: number; reason: "ok" | "exhausted" } {
  let cum = 0;
  for (const tier of DIMINISHING_TIERS) {
    cum = tier.maxRepsInTier;
    if (priorBoostedCount < cum) {
      return tier.multiplier > 1
        ? { multiplier: tier.multiplier, reason: "ok" }
        : { multiplier: 1, reason: "exhausted" };
    }
  }
  return { multiplier: 1, reason: "exhausted" };
}

/**
 * Returns the multiplier for a given action type based on the user's calling.
 * Defaults to 1× when no calling is set or the action doesn't match.
 */
export function useCallingMultiplier() {
  const { user } = useAuth();

  const { data: path } = useQuery({
    queryKey: ["calling-multiplier-path", user?.id],
    queryFn: async (): Promise<ContributionPath | null> => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("contribution_path")
        .eq("user_id", user.id)
        .maybeSingle();
      return ((data as any)?.contribution_path as ContributionPath | null) ?? null;
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  const callingPath = path ?? null;

  const multiplierFor = (actionType: string): number => {
    if (!callingPath) return 1;
    const cls = classifyActionPath(actionType);
    return cls && cls === callingPath ? CALLING_BONUS_MULTIPLIER : 1;
  };

  const isBoosted = (actionType: string): boolean => multiplierFor(actionType) > 1;

  return { callingPath, multiplierFor, isBoosted };
}